import { LIMITED_LIST_VIEW, panelViews } from './EventTimelinePanel';
import {
  OPTIONAL_MACH_EVENT_COLUMNS,
  OPTIONAL_USER_EVENT_COLUMNS,
  OPTIONAL_DTMP_EVENT_COLUMNS,
} from './EventList';
import { getGroupedUserEventOptions } from './utils';
import config from '../../../config';
import {
  SEVERITIES,
  DTMP_SEVERITIES,
  EVENT_TYPE_USER,
  EVENT_TYPE_DTMP,
  EVENT_TYPE_MACH,
} from '../../../constants';
import {
  includesEventType,
  normalizeEventTypeSelection,
  shouldShowEventTypeColumns,
} from './eventTypeSelection';

const EVENT_TYPE_TITLES = {
  [EVENT_TYPE_USER]: 'User Events',
  [EVENT_TYPE_MACH]: 'Mach IoT Events',
  [EVENT_TYPE_DTMP]: 'DTMP IoT Events',
};

const buildEventTypeSelectorProperty = (normalizedEventTypeSelection) => ({
  types: {
    title: 'Event types',
    type: 'array',
    default: normalizedEventTypeSelection,
    minItems: 1,
    uniqueItems: true,
    isMulti: true,
    items: {
      type: 'string',
      anyOf: [EVENT_TYPE_USER, EVENT_TYPE_MACH, EVENT_TYPE_DTMP].map((eventType) => ({
        enum: [eventType],
        title: EVENT_TYPE_TITLES[eventType],
      })),
    },
  },
});

const buildDefaultEventTypeProperty = (normalizedEventTypeSelection) => ({
  defaultEventType: {
    title: 'Default event type',
    type: 'string',
    default: normalizedEventTypeSelection[0],
    oneOf: normalizedEventTypeSelection.map((eventType) => ({
      enum: [eventType],
      title: EVENT_TYPE_TITLES[eventType],
    })),
  },
});

const buildHideInactiveSystemEventsProperty = (normalizedEventTypeSelection) =>
  includesEventType(normalizedEventTypeSelection, EVENT_TYPE_MACH) ||
  includesEventType(normalizedEventTypeSelection, EVENT_TYPE_DTMP)
    ? {
        hideInactiveSystemEvents: {
          title: 'Hide inactive control system',
          type: 'boolean',
          default: true,
        },
      }
    : {};

const buildMachEventTypeProperties = (
  normalizedEventTypeSelection,
  showEventTypeColumns,
  machFilterFields
) =>
  includesEventType(normalizedEventTypeSelection, EVENT_TYPE_MACH)
    ? {
        machExcludeTypes: machFilterFields.machExcludeTypes,
        ...(showEventTypeColumns ? { machColumns: machFilterFields.machColumns } : {}),
      }
    : {};

const buildDtmpEventTypeProperties = (
  normalizedEventTypeSelection,
  showEventTypeColumns,
  dtmpFilterFields
) =>
  includesEventType(normalizedEventTypeSelection, EVENT_TYPE_DTMP)
    ? {
        dtmpExcludeTypes: dtmpFilterFields.dtmpExcludeTypes,
        ...(showEventTypeColumns ? { dtmpColumns: dtmpFilterFields.dtmpColumns } : {}),
      }
    : {};

const buildUserEventTypeProperties = (
  normalizedEventTypeSelection,
  showEventTypeColumns,
  userFilterFields
) =>
  includesEventType(normalizedEventTypeSelection, EVENT_TYPE_USER)
    ? {
        userExcludeTypes: userFilterFields.userExcludeTypes,
        ...(showEventTypeColumns ? { userColumns: userFilterFields.userColumns } : {}),
      }
    : {};

const getFilters = (types, currentOptions = {}, includeDefaultEventType = false) => {
  const filters = {};
  const iotEventTypes = Object.entries(SEVERITIES).map(([k, v]) => ({
    enum: [k],
    title: v,
  }));
  const dtmpEventTypes = Object.entries(DTMP_SEVERITIES).map(([k, v]) => ({
    enum: [k],
    title: v,
  }));
  const groupedOptions = getGroupedUserEventOptions(types);
  const normalizedEventTypeSelection = normalizeEventTypeSelection(
    currentOptions?.filters?.eventTypes?.types
  );
  const showEventTypeColumns = shouldShowEventTypeColumns(currentOptions?.views);

  const userEventTypes = types?.length
    ? types.map((type) => ({
        enum: [type.id],
        title: type.name,
      }))
    : [];

  const machFilterFields = {
    machExcludeTypes: {
      type: 'array',
      title: 'Mach IoT Event types to exclude',
      default: ['internal'],
      uniqueItems: true,
      isMulti: true,
      items: {
        type: 'string',
        anyOf: iotEventTypes,
      },
    },
    machColumns: {
      type: 'array',
      title: 'Mach IoT Event List Columns (additional)',
      default: [],
      items: {
        type: 'string',
        anyOf: OPTIONAL_MACH_EVENT_COLUMNS.map((col) => ({
          enum: [col.id],
          title: col.title,
        })),
      },
      uniqueItems: true,
      isMulti: true,
    },
  };

  const userFilterFields = {
    userExcludeTypes: {
      type: 'array',
      title: 'User Event types to exclude',
      uniqueItems: true,
      isMulti: true,
      groupedOptions,
      items: {
        type: 'string',
        anyOf: userEventTypes,
      },
    },
    userColumns: {
      type: 'array',
      title: 'User Event List Columns (additional)',
      default: [],
      items: {
        type: 'string',
        anyOf: OPTIONAL_USER_EVENT_COLUMNS.filter(
          (col) => !(config.onPremise && col.id === 'assignedTo')
        ).map((col) => ({
          enum: [col.id],
          title: col.title,
        })),
      },
      uniqueItems: true,
      isMulti: true,
    },
  };

  const dtmpFilterFields = {
    dtmpExcludeTypes: {
      type: 'array',
      title: 'DTMP Event types to exclude',
      default: [],
      maxItems: dtmpEventTypes.length - 1,
      uniqueItems: true,
      isMulti: true,
      items: {
        type: 'string',
        anyOf: dtmpEventTypes,
      },
    },
    dtmpColumns: {
      type: 'array',
      title: 'DTMP IoT Event List Columns (additional)',
      default: [],
      items: {
        type: 'string',
        anyOf: OPTIONAL_DTMP_EVENT_COLUMNS.map((col) => ({
          enum: [col.id],
          title: col.title,
        })),
      },
      uniqueItems: true,
      isMulti: true,
    },
  };

  if (types?.length) {
    filters.eventTypes = {
      type: 'object',
      title: '',
      properties: {
        ...buildEventTypeSelectorProperty(normalizedEventTypeSelection),
        ...(includeDefaultEventType
          ? buildDefaultEventTypeProperty(normalizedEventTypeSelection)
          : {}),
        ...buildHideInactiveSystemEventsProperty(normalizedEventTypeSelection),
        ...buildMachEventTypeProperties(
          normalizedEventTypeSelection,
          showEventTypeColumns,
          machFilterFields
        ),
        ...buildDtmpEventTypeProperties(
          normalizedEventTypeSelection,
          showEventTypeColumns,
          dtmpFilterFields
        ),
        ...buildUserEventTypeProperties(
          normalizedEventTypeSelection,
          showEventTypeColumns,
          userFilterFields
        ),
      },
    };
  }

  return filters;
};

const getEventTimeLinePanelSchema = (types, currentOptions = {}) => {
  const hasLimitedListView = currentOptions?.views?.includes(LIMITED_LIST_VIEW);
  const panelViewTypes = panelViews.map((item) => item.type);
  const requestedViews = Array.isArray(currentOptions?.views)
    ? panelViewTypes.filter((viewType) => currentOptions.views.includes(viewType))
    : [];
  // Mirrors the panel: when no views are selected it falls back to the default views.
  const selectableViews = requestedViews.length > 0 ? requestedViews : panelViewTypes.slice(0, -1);

  return {
    title: {
      type: 'string',
      default: 'Event Timeline',
    },
    options: {
      type: 'object',
      properties: {
        views: {
          type: 'array',
          title: 'Views',
          default: panelViews.map((item) => item.type).slice(0, -1),
          items: {
            type: 'string',
            anyOf: panelViews.map((panel) => ({
              enum: [panel.type],
              title: panel.title,
            })),
          },
          uniqueItems: true,
          isMulti: true,
        },
        defaultView: {
          type: 'string',
          title: 'Default view',
          default: selectableViews[0],
          oneOf: panelViews
            .filter((panel) => selectableViews.includes(panel.type))
            .map((panel) => ({
              enum: [panel.type],
              title: panel.title,
            })),
        },
        ...(hasLimitedListView
          ? {
              limits: {
                type: 'object',
                title: 'Limited list options',
                properties: {
                  time: {
                    type: 'string',
                    title: 'Events',
                    default: 'past',
                    oneOf: [
                      { enum: ['past'], title: 'Previous' },
                      { enum: ['future'], title: 'Upcoming' },
                    ],
                  },
                  range: {
                    type: 'object',
                    title: 'Time span',
                    properties: {
                      period: {
                        type: 'string',
                        title: 'Period',
                        default: 'hours',
                        oneOf: [
                          { enum: ['minutes'], title: 'Minutes' },
                          { enum: ['hours'], title: 'Hours' },
                          { enum: ['days'], title: 'Days' },
                        ],
                      },
                      numberPeriods: {
                        type: 'number',
                        title: 'Span',
                        default: 1,
                        minimum: 1,
                        maximum: 365,
                      },
                    },
                  },
                  limit: {
                    type: 'number',
                    title: 'Number of events',
                    default: 10,
                    minimum: 1,
                    maximum: 100,
                  },
                },
              },
            }
          : {}),
        filters: {
          title: 'Filters',
          type: 'object',
          properties: getFilters(types, currentOptions, true),
        },
      },
    },
  };
};

const getEventTimeLineAdminSchema = (types = [], currentOptions = {}) => ({
  type: 'object',
  properties: {
    filters: {
      title: '',
      description: types.length > 0 ? '' : 'None available',
      type: 'object',
      properties: getFilters(types, currentOptions),
    },
  },
});

export { getEventTimeLinePanelSchema, getEventTimeLineAdminSchema };
